import { api } from '@/lib/api/client'
import type { AffiliateAllowedCampaign } from '@/types/affiliates'
import type {
  CreateMyDistributorAffiliateInput,
  FetchMyDistributorAffiliatesResponse,
  FetchMyDistributorBalanceTransactionsResponse,
  FetchMyDistributorPaymentsResponse,
  FetchMyDistributorProgramsResponse,
  MyDistributorAffiliateRegistrationMutationResponse,
  MyDistributorLoginContext,
  MyDistributorProfile,
  MyDistributorProgram,
  SearchMyDistributorAffiliateUserResponse,
  UpdateMyDistributorAffiliateInput,
} from '@/types/my-distributor'

export const DISTRIBUTOR_WORKSPACE_STORAGE_KEY = 'legalprime:distributorWorkspace'

export interface MyDistributorEmptyStateMessage {
  lines: string[]
  authHint?: string
}

export function describeMyDistributorEmptyState(
  loginContext: MyDistributorLoginContext | undefined,
  profile: MyDistributorProfile | null,
  programs: MyDistributorProgram[]
): MyDistributorEmptyStateMessage {
  const authEmail = loginContext?.auth_email?.trim() || null
  const authHint = authEmail
    ? `Conta logada: ${authEmail}`
    : loginContext?.auth_phone
      ? `Conta logada (telefone): ${loginContext.auth_phone}`
      : undefined

  const onlyInactive =
    profile !== null && programs.length > 0 && programs.every((p) => !p.is_active)

  if (onlyInactive) {
    return {
      lines: [
        'Você possui credenciamento de distribuidor, mas está inativo no momento.',
        'Peça à instituição para reativar seu perfil de distribuidor.',
      ],
      authHint,
    }
  }

  if (loginContext && !loginContext.platform_user_resolved) {
    return {
      lines: [
        'Não encontramos um usuário da plataforma vinculado ao e-mail ou telefone desta conta.',
        'Para ver Sou Distribuidor, entre com o mesmo e-mail (ou telefone) que a instituição usou ao te credenciar.',
        'Se você entrou com o e-mail da empresa (instituição), isso é diferente do e-mail pessoal credenciado como distribuidor.',
      ],
      authHint,
    }
  }

  if (profile !== null && programs.length === 0) {
    const linked = profile.email?.trim() || profile.phone || 'sua conta'
    return {
      lines: [
        `Sua identidade de distribuidor (${linked}) está cadastrada, mas nenhuma instituição aparece credenciada.`,
        'Confirme com a instituição se o credenciamento foi concluído e está ativo.',
      ],
      authHint,
    }
  }

  return {
    lines: [
      'Você ainda não está credenciado como distribuidor em nenhuma instituição.',
      'O credenciamento é feito pela instituição em Meus Distribuidores.',
      'Depois, acesse Sou Distribuidor com o mesmo e-mail ou telefone usado no credenciamento.',
    ],
    authHint,
  }
}

/** Credenciado como distribuidor em ao menos uma instituição (programa Sou Distribuidor). */
export function userHasDistributorParticipation(
  programs: MyDistributorProgram[] | undefined
): boolean {
  return Array.isArray(programs) && programs.length > 0
}

export async function fetchMyDistributorPrograms(): Promise<FetchMyDistributorProgramsResponse> {
  return api.get<FetchMyDistributorProgramsResponse>('/api/my-distributor/programs', {
    requireAuth: true,
  })
}

export async function fetchMyDistributorPaymentsAll(
  params: {
    distributor_id?: number
    campaign_id?: number
    page?: number
    per_page?: number
  } = {}
): Promise<FetchMyDistributorPaymentsResponse> {
  const searchParams = new URLSearchParams()
  if (params.distributor_id != null) {
    searchParams.set('distributor_id', String(params.distributor_id))
  }
  if (params.campaign_id != null) searchParams.set('campaign_id', String(params.campaign_id))
  if (params.page != null) searchParams.set('page', String(params.page))
  if (params.per_page != null) searchParams.set('per_page', String(params.per_page))
  const query = searchParams.toString()

  return api.get<FetchMyDistributorPaymentsResponse>(
    `/api/my-distributor/payments${query ? `?${query}` : ''}`,
    { requireAuth: true }
  )
}

export async function fetchMyDistributorPayments(
  distributorId: number,
  params: { campaign_id?: number; page?: number; per_page?: number } = {}
): Promise<FetchMyDistributorPaymentsResponse> {
  const searchParams = new URLSearchParams()
  if (params.campaign_id != null) {
    searchParams.set('campaign_id', String(params.campaign_id))
  }
  if (params.page != null) searchParams.set('page', String(params.page))
  if (params.per_page != null) searchParams.set('per_page', String(params.per_page))
  const query = searchParams.toString()

  return api.get<FetchMyDistributorPaymentsResponse>(
    `/api/my-distributor/programs/${distributorId}/payments${query ? `?${query}` : ''}`,
    { requireAuth: true }
  )
}

export async function fetchMyDistributorBalanceTransactions(
  distributorId: number,
  params: { campaign_id?: number; page?: number; per_page?: number } = {}
): Promise<FetchMyDistributorBalanceTransactionsResponse> {
  const searchParams = new URLSearchParams()
  if (params.campaign_id != null) searchParams.set('campaign_id', String(params.campaign_id))
  if (params.page != null) searchParams.set('page', String(params.page))
  if (params.per_page != null) searchParams.set('per_page', String(params.per_page))
  const query = searchParams.toString()

  return api.get<FetchMyDistributorBalanceTransactionsResponse>(
    `/api/my-distributor/programs/${distributorId}/balance-transactions${query ? `?${query}` : ''}`,
    { requireAuth: true }
  )
}

export async function fetchMyDistributorAffiliates(
  distributorId: number,
  params: { campaign_id?: number } = {}
): Promise<FetchMyDistributorAffiliatesResponse> {
  const searchParams = new URLSearchParams()
  if (params.campaign_id != null) searchParams.set('campaign_id', String(params.campaign_id))
  const query = searchParams.toString()

  return api.get<FetchMyDistributorAffiliatesResponse>(
    `/api/my-distributor/programs/${distributorId}/affiliates${query ? `?${query}` : ''}`,
    { requireAuth: true }
  )
}

export async function searchMyDistributorAffiliateUser(
  distributorId: number,
  input: { email?: string; phone?: string }
): Promise<SearchMyDistributorAffiliateUserResponse> {
  return api.post<SearchMyDistributorAffiliateUserResponse>(
    `/api/my-distributor/programs/${distributorId}/affiliates/users/search`,
    input,
    { requireAuth: true }
  )
}

export async function createMyDistributorAffiliate(
  distributorId: number,
  input: CreateMyDistributorAffiliateInput
): Promise<MyDistributorAffiliateRegistrationMutationResponse> {
  return api.post<MyDistributorAffiliateRegistrationMutationResponse>(
    `/api/my-distributor/programs/${distributorId}/affiliates`,
    input,
    { requireAuth: true }
  )
}

export async function updateMyDistributorAffiliate(
  distributorId: number,
  registrationId: number,
  input: UpdateMyDistributorAffiliateInput
): Promise<MyDistributorAffiliateRegistrationMutationResponse> {
  return api.put<MyDistributorAffiliateRegistrationMutationResponse>(
    `/api/my-distributor/programs/${distributorId}/affiliates/registrations/${registrationId}`,
    input,
    { requireAuth: true }
  )
}

function resolveHttpsExternalPageUrl(value: string | null | undefined): string | null {
  const trimmed = value?.trim() ?? ''
  if (trimmed === '') {
    return null
  }

  try {
    const url = new URL(trimmed)
    if (url.protocol !== 'https:') {
      return null
    }

    return url.toString()
  } catch {
    return null
  }
}

export function buildMyDistributorAffiliateLink(
  campaign: Pick<AffiliateAllowedCampaign, 'slug' | 'public_page_url'>,
  trackingCode: string,
  origin: string = typeof window !== 'undefined' ? window.location.origin : ''
): string {
  const externalBase = resolveHttpsExternalPageUrl(campaign.public_page_url)
  if (externalBase !== null) {
    const url = new URL(externalBase)
    url.searchParams.set('af', trackingCode)

    return url.toString()
  }

  return `${origin}/campanha/${campaign.slug}?af=${encodeURIComponent(trackingCode)}`
}

export function formatMyDistributorCurrency(value: number): string {
  return value.toLocaleString('pt-BR', { style: 'currency', currency: 'BRL' })
}

export function activeDistributorPrograms(programs: MyDistributorProgram[]): MyDistributorProgram[] {
  return programs.filter((p) => p.is_active)
}

export function manualDistributorPrograms(programs: MyDistributorProgram[]): MyDistributorProgram[] {
  return programs.filter((p) => p.payout_method === 'manual')
}

export function findProgramByInstitutionId(
  programs: MyDistributorProgram[],
  institutionId: number
): MyDistributorProgram | undefined {
  return programs.find((p) => p.institution.id === institutionId)
}

export function resolveMaxAffiliateCommissionPercent(
  program: MyDistributorProgram,
  campaignId?: number | null
): number {
  if (campaignId != null) {
    const campaign = program.allowed_campaigns.find((c) => c.id === campaignId)
    if (campaign?.max_affiliate_commission_percent != null) {
      return campaign.max_affiliate_commission_percent
    }
  }

  return program.max_affiliate_commission_percent
}
